import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { SM_BREAKPOINT, observeBreakpoint } from "@bitwarden/web-vault/app/shared/breakpoints";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import {
  AddBalanceWizardResult,
  STAGE_ORDER,
} from "@bitwarden/web-vault/app/components/add-balance-wizard/add-balance-wizard.component";
import { StatementAccountWithBalances } from "@bitwarden/web-vault/app/components/add-balance-wizard/select-account-stage.component";
import { FileUploadComponent } from "@bitwarden/web-vault/app/components/file-upload/file-upload.component";
import {
  CabinetFileType,
  CabinetFileUploadedData,
} from "@bitwarden/web-vault/app/models/types/cabinet.types";
import { transactionDataType } from "@bitwarden/web-vault/app/models/types/transactionData.types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { TransactionService } from "@bitwarden/web-vault/app/services/DataService/transaction/transaction.service";
import { CabinetService } from "@bitwarden/web-vault/app/services/DataService/vault-file/cabinet.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { BookBalance } from "@bitwarden/web-vault/app/shared/utils/helper.account/balance";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { FileUploadService } from "@bitwarden/web-vault/app/services/DataService/file-upload/file-upload.service";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { StatementImportResult } from "@bitwarden/web-vault/app/gloss/cabinet/statement-import-mapping/statement-import-mapping.component";
import { MappedStatementRow } from "@bitwarden/web-vault/app/shared/utils/file-upload/statement-mapping.util";
import { CategoryUtils } from "@bitwarden/web-vault/app/models/view/category/category.utils";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";

// NOTE: global side-effect import (not styleUrls) to match the repo-wide convention —
// no component here uses styleUrls; the custom webpack build loads component SCSS this way.
import "./cabinet-file-upload.component.scss";

@Component({
  selector: "app-cabinet-file-upload",
  templateUrl: "./cabinet-file-upload.component.html",
})
export class CabinetFileUploadComponent implements OnDestroy {
  private i18nService = inject(I18nService);

  // Input properties to replace dialog data
  @Input() closeForm?: (data: boolean) => void;
  @Input() showComponent = true;

  // Output events
  @Output() closed = new EventEmitter<CabinetFileUploadedData>();
  @Output() formStepChanged = new EventEmitter<string>();

  // Used to reset the upload widget when the file is cleared programmatically.
  @ViewChild(FileUploadComponent) fileUploadComponent?: FileUploadComponent;

  selectedFile: File | null = null;
  editedFileName: string | null = null;
  chooseAccountDialog = true;
  uploadFileData: CabinetFileUploadedData;
  uploading = false;
  transactions: transactionDataType[] = [];

  /** Current stage reported by the add-balance wizard. */
  wizardStage = "file-details";
  /** When on the add-transaction stage, the file preview is hidden unless the user opts in. */
  showPreview = false;

  /** True on mobile (Tailwind `sm` breakpoint, under 640px). Mobile shows the back button in the
   *  modal header instead of inline. */
  // Matches Tailwind's `sm` breakpoint used by the action-button styles.
  isMobile = observeBreakpoint(SM_BREAKPOINT);

  /** Mapped statement rows from the column-mapping dialog; fed to the file-upload preview. */
  statementMappedRows: MappedStatementRow[] = [];
  /** Parsed statement; prefills the add-balance wizard (dates, account, balances, transactions). */
  statementImport: StatementImportResult | null = null;
  /** True once the file has been persisted, so a retry after a transaction failure does not
   *  re-save it and create a duplicate VaultFile. */
  private fileSaved = false;
  /** Guards against emitting `closed` more than once (e.g. cancel during an in-flight save). */
  private closedEmitted = false;

  private destroy$ = new Subject<void>();

  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private transactionService: TransactionService = inject(TransactionService);
  private cabinetService: CabinetService = inject(CabinetService);
  private fileUploadService: FileUploadService = inject(FileUploadService);
  private userStore: UserStoreService = inject(UserStoreService);
  private appState: AppStateStoreService = inject(AppStateStoreService);
  private dialogFactory: DialogFactory = new DialogFactory();
  private categoryUtils = new CategoryUtils();

  protected selectedAccount: WritableSignal<AccountView> = signal(null);

  constructor() {
    // Initialize with balance step
    this.formStepChanged.emit("balance");
  }

  receivedSelectedFile(event: File | null) {
    this.selectedFile = event ?? null;
    this.editedFileName = null; // Reset edited name when new file is selected (or cleared)
    // Any new (or cleared) file drops the previous statement import, so a cancelled
    // re-map can't leave stale prefill/preview rows bound to the new file.
    this.clearStatementImport();
    if (!event) {
      return;
    }
    // Tabular sources (csv / xls / xlsx) open the column-mapping dialog so rows can be
    // mapped + parsed; non-tabular files (e.g. pdf) preview as-is and are entered manually.
    if (this.fileUploadService.isTabular(event)) {
      this.openMappingDialog(event);
    }
  }

  private openMappingDialog(file: File): void {
    let mapped = false;
    // Accounts created via "Create new account" inside the dialog persist immediately.
    // Track them so we can roll back any that a completed import didn't actually use
    // (or all of them, if the dialog is cancelled) — otherwise they leak as orphans.
    const createdAccounts: AccountView[] = [];
    const ref = this.dialogFactory.openStatementImportMapping(
      file,
      (result) => {
        mapped = true;
        this.applyImportedStatement(result);
      },
      (account) => createdAccounts.push(account),
    );
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const keptAccountId = mapped ? this.statementImport?.account?.id : undefined;
        void this.rollbackUnusedAccounts(createdAccounts, keptAccountId);
        // Closed without mapping and nothing mapped before -> treat as "no file chosen".
        if (!mapped && this.statementMappedRows.length === 0) {
          this.clearSelectedFile();
        }
      });
  }

  /** Delete accounts created inside the mapping dialog that the import didn't keep. */
  private async rollbackUnusedAccounts(
    created: AccountView[],
    keptAccountId?: string,
  ): Promise<void> {
    for (const account of created) {
      if (account.id === keptAccountId) {
        continue;
      }
      try {
        await this.userStore.accounts.delete(account);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to roll back account created during statement import", e);
      }
    }
  }

  /** Mapped statement: show the mapped preview and prefill the wizard. */
  private applyImportedStatement(result: StatementImportResult): void {
    if (!result?.account) {
      return;
    }
    this.statementMappedRows = result.mappedRows ?? [];
    this.statementImport = result;
    this.selectedAccount.set(result.account);
  }

  /** Deselect the file: clears the upload widget, preview pane and pending import state. */
  clearSelectedFile(): void {
    this.selectedFile = null;
    this.clearStatementImport();
    this.fileUploadComponent?.clearFileSelected(new Event("click"));
  }

  private clearStatementImport(): void {
    this.statementMappedRows = [];
    this.statementImport = null;
    this.selectedAccount.set(null);
    this.fileSaved = false;
  }

  handleClose(): void {
    if (this.uploading) {
      return;
    }
    this.emitClosed();
  }

  /** Emits `closed` at most once; extra calls (e.g. cancel during an in-flight save) are ignored. */
  private emitClosed(): void {
    if (this.closedEmitted) {
      return;
    }
    this.closedEmitted = true;
    this.closed.emit(this.uploadFileData);
  }

  onFileNameChanged(newFileName: string): void {
    this.editedFileName = newFileName;
  }

  onWizardStageChanged(stage: string): void {
    this.wizardStage = stage;
  }

  /** On mobile the add-transaction stage takes over the full width, so the preview pane is collapsed there. */
  get isAddTransactionStage(): boolean {
    return this.wizardStage === "add-transaction";
  }

  /** Index of the current wizard stage, used to light up the stage indicator bars. */
  get wizardStageIndex(): number {
    return STAGE_ORDER.indexOf(this.wizardStage as (typeof STAGE_ORDER)[number]);
  }

  /** Heading shown beside the stage bar (desktop only) - changes per stage. */
  get wizardStageTitle(): string {
    switch (this.wizardStage) {
      case "select-account":
        return this.i18nService.t("linkToAccounts");
      case "add-transaction":
        return this.i18nService.t("addTransactions");
      default:
        return this.i18nService.t("uploadStatement");
    }
  }

  /**
   * Whether the file-preview column is visible on mobile. Desktop always shows it via responsive
   * classes; on mobile it is hidden on the add-transaction stage unless the user opts back in.
   */
  get isPreviewVisible(): boolean {
    return !this.isAddTransactionStage || this.showPreview;
  }

  async handleSaveAndAddLater(uploadData: AddBalanceWizardResult): Promise<void> {
    // Guard against double-submits: ignore extra clicks while a save is in flight. `uploading`
    // covers the whole handler (file + transaction save) so a rapid second click can't re-enter
    // during the transaction phase; it resets in the finally so a failed save can still be retried.
    if (this.uploading) {
      return;
    }
    this.uploading = true;

    this.appState.startProcess("statement-import", "Importing statement...");
    try {
      // Prepare the cabinet file uploaded data
      const cabinetFileData: CabinetFileUploadedData = {
        name: this.editedFileName || this.selectedFile?.name,
        mimeType: this.selectedFile?.type as CabinetFileType,
        statementFromDate: uploadData.statementFromDate,
        statementToDate: uploadData.statementToDate,
        statementAccounts: uploadData.accounts.map((acc) => acc.account),
        openingBalances: this.convertBalancesToMap(uploadData.accounts, "openingBalance"),
        closingBalances: this.convertBalancesToMap(uploadData.accounts, "closingBalance"),
        file: this.selectedFile,
      };

      // Only close once the save is confirmed; on failure the error toast is shown and the
      // overlay stays open so the user can retry. Skip re-saving on a retry whose file already
      // persisted (a later transaction failure) so we don't create a duplicate VaultFile.
      if (!this.fileSaved) {
        const saved = await this.saveUploadFile(this.selectedFile, cabinetFileData);
        if (!saved) {
          return;
        }
        this.fileSaved = true;
      }

      // Persist transactions captured in the wizard's add-transaction stage (empty for the
      // "save and add later" path). Without this they're collected but silently dropped.
      this.transactions = uploadData.transactions ?? [];
      if (this.transactions.length) {
        const transactionsSaved = await this.saveAllTransactions();
        if (!transactionsSaved) {
          return;
        }
      }

      this.uploadFileData = cabinetFileData;
      this.emitClosed();
    } finally {
      this.uploading = false;
      this.appState.endProcess("statement-import");
    }
  }

  private convertBalancesToMap(
    accounts: StatementAccountWithBalances[],
    balanceType: "openingBalance" | "closingBalance",
  ): Map<string, BookBalance> {
    const balanceMap = new Map<string, BookBalance>();
    accounts.forEach((acc) => {
      const balance = acc[balanceType];
      if (balance instanceof BookBalance) {
        balanceMap.set(acc.account.id, balance);
      }
    });
    return balanceMap;
  }

  async saveAllTransactions(): Promise<boolean> {
    try {
      for (const transaction of this.transactions) {
        await this.resolveTransactionCategory(transaction);
      }
      await this.transactionService.createNormalTransactions(this.transactions);
      this.notifyService.notify(
        NotificationEnum.success,
        "success",
        "allTransactionsAddedSuccessfully",
      );
      this.selectedAccount.set(null);
      return true;
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "error", "transactionFailed");
      return false;
    }
  }

  private async resolveTransactionCategory(transaction: transactionDataType): Promise<void> {
    const categoryName = transaction.formData.categoryName?.trim();
    if (!categoryName || transaction.formData.categoryId) {
      delete transaction.formData.categoryName;
      return;
    }

    const category = await this.categoryUtils.getOrAddCategory(categoryName, this.userStore);
    if (category?.id) {
      transaction.formData.categoryId = category.id;
    }
    delete transaction.formData.categoryName;
  }

  /** Returns true only when the file was saved, so callers can avoid closing/advancing on failure. */
  async saveUploadFile(selectedFile: File, newBlance: CabinetFileUploadedData): Promise<boolean> {
    try {
      const data: CabinetFileUploadedData = {
        name: this.editedFileName || selectedFile?.name,
        mimeType: selectedFile?.type as CabinetFileType,
        statementToDate: newBlance.statementToDate,
        statementFromDate: newBlance.statementFromDate,
        statementAccounts: newBlance.statementAccounts,
        openingBalances: newBlance.openingBalances,
        closingBalances: newBlance.closingBalances,
        file: selectedFile,
      };

      const isSaved = await this.cabinetService.saveFile(data);
      if (isSaved && this.closeForm) {
        this.closeForm(isSaved);
      }
      return isSaved;
    } catch (error) {
      this.cabinetService.displayCatchError(error);
      return false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
